import React, { useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { ReactComponent as MenuIconWhite } from '../../../../assets/icons/MenuDots.svg';
import { ReactComponent as MenuIconBlack } from '../../../../assets/icons/MenuDotsB.svg';
import { ReactComponent as ListIcon } from '../../../../assets/icons/list.svg';
import { formatUKDate } from '../../../../utils';
import '../../NotesTable/NotesTable.scss';
import { FollowOnItem } from '../../../../api/follow-on';

export interface FollowOnAlert extends FollowOnItem { }

interface FollowOnAlertsTableProps {
  alerts: FollowOnAlert[];
  isLoading: boolean;
  onEdit: (alert: FollowOnAlert) => void;
  onDelete: (alertId: number) => void;
  isReadOnly?: boolean;
}

const FollowOnAlertsTable: React.FC<FollowOnAlertsTableProps> = ({
  alerts,
  isLoading,
  onEdit,
  onDelete,
  isReadOnly = false
}) => {
  const [openMenuIds, setOpenMenuIds] = useState<number[]>([]);

  const toggleMenu = (id: number) => {
    setOpenMenuIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const handleMenuClick = (alertId: number, action: string) => {
    const alert = alerts.find(a => a.id === alertId);

    if (!alert) return;

    if (action === 'view') {
      onEdit(alert);
    } else if (action === 'edit') {
      onEdit(alert);
    } else if (action === 'delete') {
      onDelete(alertId);
    }
    setOpenMenuIds([]);
  };

  const actionMenuStyles = (alertId: number) => ({
    backgroundColor: openMenuIds.includes(alertId) ? '#238698' : 'transparent',
  });

  if (isLoading) {
    return (
      <div className='notes-container'>
        <div className='notes-table'>
          <div className='table-header'>
            <div className='header-cell'>Created at</div>
            <div className='header-cell'>Updated at</div>
            <div className='header-cell'>Description</div>
            <div className='header-cell'>Created by</div>
            <div className='header-cell'></div>
          </div>
          <div className='table-body'>
            <div className='loading-state'>
              <div className='loading-spinner' />
              <div className='loading-text'>Loading follow-on alerts...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='notes-container'>
      <div className='notes-table'>
        <div className='table-header'>
          <div className='header-cell'>Created at</div>
          <div className='header-cell'>Updated at</div>
          <div className='header-cell'>Description</div>
          <div className='header-cell'>Created by</div>
          <div className='header-cell'></div>
        </div>
        <div className='table-body'>
          {alerts.length === 0 ? (
            <div className='empty-state'>
              <div className='empty-text'>No follow-on alerts found</div>
              <div className='empty-subtext'>Create a follow-on to see it here.</div>
            </div>
          ) : (
            alerts.map(alert => (
              <div key={alert.id} className='table-row'>
                <div className='table-cell'>
                  <div>
                    <div>{formatUKDate(alert.created_at)}</div>
                    <div>{formatUKDate(alert.created_at, 'hh:mm a')}</div>
                  </div>
                </div>
                <div className='table-cell'>
                  <div>
                    <div>{formatUKDate(alert.updated_at)}</div>
                    <div>{formatUKDate(alert.updated_at, 'hh:mm a')}</div>
                  </div>
                </div>
                <div className='table-cell'>
                  {alert.description ? stripHtml(alert.description) : '-'}

                </div>
                <div className='table-cell'>
                  {`${alert.author.first_name} ${alert.author.last_name}`}
                </div>
                <div className='table-cell actions'>
                  {!isReadOnly && (
                    <div className='action-menu-container'>
                      <div
                        className='menu-btn'
                        style={actionMenuStyles(alert.id)}
                        onClick={() => toggleMenu(alert.id)}
                      >
                        {openMenuIds.includes(alert.id) ? <MenuIconWhite /> : <MenuIconBlack />}
                      </div>

                      {openMenuIds.includes(alert.id) && (
                        <OutsideClickHandler onOutsideClick={() => setOpenMenuIds([])}>
                          <div className='action-menu'>
                            <div className='option' onClick={(e) => {
                              e.stopPropagation();
                              handleMenuClick(alert.id, 'view');
                            }}>
                              <ListIcon /> View/Edit
                            </div>
                          </div>
                        </OutsideClickHandler>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowOnAlertsTable;
